import { useState, useEffect, useCallback } from "react";
import { isTossApp } from "@/lib/platform";

type Theme = "light" | "dark" | "system";

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: Theme) {
  // 토스 인앱: 비게임 앱은 라이트 모드 전용 (TOSS_GUIDE 규정)
  if (isTossApp()) {
    document.documentElement.classList.remove("dark");
    return;
  }
  const resolved = theme === "system" ? getSystemTheme() : theme;
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    // 토스 인앱: 강제 라이트 모드
    if (isTossApp()) return "light";
    const saved = localStorage.getItem("steamdeal-theme") as Theme | null;
    return saved || "system";
  });

  const setTheme = useCallback((t: Theme) => {
    // 토스 인앱: 테마 변경 차단
    if (isTossApp()) return;
    setThemeState(t);
    localStorage.setItem("steamdeal-theme", t);
    applyTheme(t);
  }, []);

  useEffect(() => {
    applyTheme(theme);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => { if (theme === "system") applyTheme("system"); };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const cycle = useCallback(() => {
    if (isTossApp()) return; // 토스에서는 테마 순환 비활성화
    const next: Record<Theme, Theme> = { light: "dark", dark: "system", system: "light" };
    setTheme(next[theme]);
  }, [theme, setTheme]);

  return { theme, setTheme, cycle };
}
